import { createResolverRegistry } from '../resolvers/registry/resolverRegistry';
import { createResourceLifecycleManager } from '../lifecycleManager/resourceLifecycleManager';
import { create } from 'event-emitter';
import { defaultResourceRegistry } from '../resources/resourceRegistry';
import { createChangeManager } from './changeManager';
import { createScheduler } from '../scheduler/scheduler';
import { createPollManager } from '../polling/pollManager';
import { createGroupManager } from '../groups/groupManager';
import { createEmissionBuffer } from './emissionBuffer';
import { indexedMap } from '../utils/indexedMap';
import { withTrackedResolvers } from '../utils/withResolvers';
import { createLogger } from '../devtools/logger';
import { maybeLoadDevtools } from '../devtools/maybeLoadDevtools';
import { __mockInternal_isEnabled, __mockInternal_getMockResolver, __mockInternal_registerClient } from '../mockInternal';
import { createPersistenceManager } from '../persistence/persistenceManager';
let __TEST_ONLY_DISABLE_DEFER_UNWATCH = false;
export const __testOnlyDisableDeferUnwatch = () => {
  __TEST_ONLY_DISABLE_DEFER_UNWATCH = true;
};
export const __testOnlyEnableDeferUnwatch = () => {
  __TEST_ONLY_DISABLE_DEFER_UNWATCH = false;
};
const constructClient = ({
  resourceRegistry = defaultResourceRegistry,
  httpClient: clientLevelHttpClient
} = {}) => {
  const deferUnwatchScheduler = createScheduler();
  const promiseCache = indexedMap();
  const logger = createLogger();
  const devtools = maybeLoadDevtools({
    logger
  });
  const changeManager = createChangeManager({
    devtools: devtools.callbacks
  });
  logger === null || logger === void 0 || logger.debug('startup');
  const stateEmitter = create({
    onEmitError: ({
      error
    }) => {
      logger === null || logger === void 0 || logger.error('error emitting state', {
        error: error.message
      });
      return false;
    }
  });
  const emissionBuffer = createEmissionBuffer({
    onFlush: (instanceKey, state) => {
      var _promiseCache$get, _promiseCache$get2;
      if (!state.loading || (_promiseCache$get = promiseCache.get(instanceKey, 'fresh')) !== null && _promiseCache$get !== void 0 && _promiseCache$get._resolved) {
        promiseCache.delete(instanceKey, 'fresh');
      }
      if (!state.loading || (_promiseCache$get2 = promiseCache.get(instanceKey, 'stale')) !== null && _promiseCache$get2 !== void 0 && _promiseCache$get2._resolved) {
        promiseCache.delete(instanceKey, 'stale');
      }
      stateEmitter.emit(instanceKey, state);
    }
  });
  const persistenceManager = createPersistenceManager();
  const resolverRegistry = createResolverRegistry({
    logger: logger === null || logger === void 0 ? void 0 : logger.withTags({
      component: 'resolverRegistry'
    }),
    persistenceManager
  });
  const groupManager = createGroupManager({
    logger: logger === null || logger === void 0 ? void 0 : logger.withTags({
      component: 'groupManager'
    })
  });
  const lifecycleManager = createResourceLifecycleManager({
    devtools: devtools.callbacks,
    logger: logger === null || logger === void 0 ? void 0 : logger.withTags({
      component: 'lifecycleManager'
    }),
    resourceRegistry,
    groupManager,
    changeManager,
    persistenceManager,
    onNodeCollected: instanceKey => {
      var _devtools$callbacks;
      promiseCache.delete(instanceKey, 'fresh');
      promiseCache.delete(instanceKey, 'stale');
      (_devtools$callbacks = devtools.callbacks) === null || _devtools$callbacks === void 0 || _devtools$callbacks.onNodeCollected(instanceKey);
    },
    onStateChange: (instanceKey, state, changeContext) => {
      emissionBuffer.schedule(instanceKey, state);
      logger === null || logger === void 0 || logger.debug('received state change notification', {
        instanceKey,
        changeId: changeContext.changeId,
        originId: changeContext.originId,
        loading: state.loading,
        stale: state.stale,
        error: !!state.error
      });
    },
    onInvalidate: ({
      instanceKey,
      triggeringContext
    }) => {
      const nextContext = changeManager.cascade(triggeringContext);
      const invalidateLogger = logger === null || logger === void 0 ? void 0 : logger.withTags({
        instanceKey,
        changeId: nextContext === null || nextContext === void 0 ? void 0 : nextContext.changeId,
        originId: nextContext === null || nextContext === void 0 ? void 0 : nextContext.originId
      });
      invalidateLogger === null || invalidateLogger === void 0 || invalidateLogger.info('received invalidation request event');
      if (resolverRegistry.isAttached(instanceKey)) {
        invalidateLogger === null || invalidateLogger === void 0 || invalidateLogger.info('requesting resolver refresh');
        resolverRegistry.refresh(instanceKey, nextContext);
      } else {
        invalidateLogger === null || invalidateLogger === void 0 || invalidateLogger.debug('requested invalidation of non-attached resource');
      }
    }
  });
  const refreshInstance = instanceKey => {
    if (resolverRegistry.isAttached(instanceKey)) {
      resolverRegistry.refresh(instanceKey, changeManager.start('poll'));
    }
  };
  const pollManager = createPollManager({
    logger: logger === null || logger === void 0 ? void 0 : logger.withTags({
      component: 'pollManager'
    }),
    onPoll: key => {
      if (groupManager.has(key)) {
        for (const instanceKey of groupManager.get(key)) {
          refreshInstance(instanceKey);
        }
      } else {
        refreshInstance(key);
      }
    }
  });
  const watch = ({
    resource,
    params = {},
    instanceKey = resource.identifyInstance(params),
    pollInterval,
    httpClient,
    pollGroup
  }, callback, __stopAtResolvedSources) => {
    const watchLogger = logger === null || logger === void 0 ? void 0 : logger.withTags({
      instanceKey
    });
    watchLogger === null || watchLogger === void 0 || watchLogger.info('watching resource');

    // Clear a deferred unwatch
    if (deferUnwatchScheduler.clear(instanceKey)) {
      watchLogger === null || watchLogger === void 0 || watchLogger.debug('cleared deferred unwatch');
    }

    // Mock interception (test-only, DCE'd in production)
    let __mockResolver = null;
    if (process.env.NODE_ENV !== 'production') {
      if (__mockInternal_isEnabled()) {
        __mockResolver = __mockInternal_getMockResolver(resource);
      }
    }

    // Watch sources
    // Mock boundary: skip source watching when a mock resolver is active
    let sourceUnwatches = null;
    if (resource.sources && !__mockResolver) {
      for (const source of resource.sources) {
        var _sourceUnwatches;
        if (__stopAtResolvedSources && source.resource.resolver) {
          continue;
        }
        ((_sourceUnwatches = sourceUnwatches) !== null && _sourceUnwatches !== void 0 ? _sourceUnwatches : sourceUnwatches = []).push(watch({
          resource: source.resource,
          params: source.mapParams(params),
          pollInterval,
          httpClient,
          pollGroup
        }, undefined, __stopAtResolvedSources));
      }
    }

    // If needed, register the poll interval
    let unregisterPoll;
    if (pollInterval && pollInterval > 0) {
      if (pollGroup) {
        groupManager.add(pollGroup, instanceKey);
        const unregisterInterval = pollManager.register(pollGroup, pollInterval);
        unregisterPoll = () => {
          unregisterInterval();
          groupManager.remove(pollGroup, instanceKey);
        };
      } else {
        unregisterPoll = pollManager.register(instanceKey, pollInterval);
      }
    }

    // Subscribe to state changes
    // Note: The callback's fallback must be a new reference per-watch
    const off = stateEmitter.on(instanceKey, callback !== null && callback !== void 0 ? callback : () => {});

    // Undo all the work we just did
    const unwatch = () => {
      var _unregisterPoll;
      watchLogger === null || watchLogger === void 0 || watchLogger.info('unwatching resource');

      // Unwatch sources and unregister poll for this watcher unconditionally.
      // Each source has its own refcount, so this cascades correctly.
      // pollManager has its own internal refcounting per interval.
      if (sourceUnwatches) {
        for (const unwatchSource of sourceUnwatches) {
          unwatchSource();
        }
      }
      (_unregisterPoll = unregisterPoll) === null || _unregisterPoll === void 0 || _unregisterPoll();
      if (off() === 0) {
        watchLogger === null || watchLogger === void 0 || watchLogger.debug('queueing detach');
        const detach = () => {
          watchLogger === null || watchLogger === void 0 || watchLogger.info('executing detach');

          // Detach the resolver, if needed
          if (resolverRegistry.isAttached(instanceKey)) {
            resolverRegistry.detach(instanceKey);
          }

          // Detach the resource
          lifecycleManager.detach(instanceKey);
        };
        if (__TEST_ONLY_DISABLE_DEFER_UNWATCH) {
          detach();
        } else {
          deferUnwatchScheduler.queue(instanceKey, 1000, detach);
        }
      }
    };
    if (!lifecycleManager.isAttached(instanceKey)) {
      var _mockResolver;
      watchLogger === null || watchLogger === void 0 || watchLogger.info('attaching resource');
      const resolverToAttach = (_mockResolver = __mockResolver) !== null && _mockResolver !== void 0 ? _mockResolver : resource.resolver;
      if (resolverToAttach) {
        resolverRegistry.attach({
          resource,
          instanceKey,
          params,
          resolver: resolverToAttach,
          httpClient: httpClient !== null && httpClient !== void 0 ? httpClient : clientLevelHttpClient,
          getState: () => lifecycleManager.getState(instanceKey),
          onResolverUpdate: (resolverState, meta) => {
            var _meta$changeContext, _meta$changeContext2;
            watchLogger === null || watchLogger === void 0 || watchLogger.info('received state update from resolver', {
              changeId: meta === null || meta === void 0 || (_meta$changeContext = meta.changeContext) === null || _meta$changeContext === void 0 ? void 0 : _meta$changeContext.changeId,
              originId: meta === null || meta === void 0 || (_meta$changeContext2 = meta.changeContext) === null || _meta$changeContext2 === void 0 ? void 0 : _meta$changeContext2.originId,
              loading: resolverState.loading,
              stale: resolverState.stale,
              error: !!resolverState.error
            });
            lifecycleManager.handleStateChange(instanceKey, params, meta !== null && meta !== void 0 && meta.changeContext ? changeManager.next(meta.changeContext) : changeManager.start('resolver-update'), resolverState);
          }
        });
      }
      lifecycleManager.attach({
        params,
        instanceKey,
        // Treat initializations as cascades so that we don't trigger
        // downstream invalidations. This is an (admittedly imperfect) method to
        // prevent refresh storms when lots of invalidation-linked nodes all init at once
        changeContext: changeManager.cascade(changeManager.start('init'))
      });
    }
    return unwatch;
  };
  const getState = ({
    resource,
    params = {},
    instanceKey = resource.identifyInstance(params),
    materialize = true
  }) => {
    const existing = lifecycleManager.getState(instanceKey);
    if (existing || !materialize) {
      return existing;
    }
    return lifecycleManager.materialize({
      instanceKey,
      params,
      changeContext: changeManager.start('materialize')
    });
  };
  const invalidate = ({
    resource,
    params = {},
    instanceKey = resource.identifyInstance(params)
  }) => {
    const changeContext = changeManager.start('invalidate');
    const invalidateLogger = logger === null || logger === void 0 ? void 0 : logger.withTags({
      instanceKey,
      changeId: changeContext.changeId,
      originId: changeContext.originId
    });
    invalidateLogger === null || invalidateLogger === void 0 || invalidateLogger.info('invalidating resource');
    lifecycleManager.invalidate(instanceKey, changeContext);
  };
  const invalidateAll = ({
    resource: {
      typeName
    }
  }) => {
    const changeContext = changeManager.start('invalidate-all');
    const invalidateLogger = logger === null || logger === void 0 ? void 0 : logger.withTags({
      typeName,
      changeId: changeContext.changeId,
      originId: changeContext.originId
    });
    invalidateLogger === null || invalidateLogger === void 0 || invalidateLogger.info('invalidating all resources of type');
    lifecycleManager.invalidateAll(typeName, changeContext);
  };
  const setState = ({
    resource,
    state,
    params = {},
    instanceKey = resource.identifyInstance(params),
    optimistic = false
  }) => {
    const changeContext = changeManager.start('set-state', {
      optimistic
    });
    const setStateLogger = logger === null || logger === void 0 ? void 0 : logger.withTags({
      instanceKey,
      changeId: changeContext.changeId,
      originId: changeContext.originId
    });
    const nextState = typeof state === 'function' ? state(lifecycleManager.getState(instanceKey)) : state;
    if (nextState) {
      var _unwatch;
      setStateLogger === null || setStateLogger === void 0 || setStateLogger.info('setting state');
      let unwatch;

      // watch the full chain for derived resources to ensure all links are available
      if (resource.sources && !lifecycleManager.isAttached(instanceKey)) {
        unwatch = watch({
          resource,
          params,
          instanceKey
        }, undefined, true);
      }
      lifecycleManager.handleStateChange(instanceKey, params, changeContext, nextState);

      // Unwatch after — not strictly necessary at runtime as detachment is deferred,
      // but tests disable the deferral
      (_unwatch = unwatch) === null || _unwatch === void 0 || _unwatch();
    } else {
      setStateLogger === null || setStateLogger === void 0 || setStateLogger.debug('functional state update returned void');
    }
  };
  const getPromise = ({
    resource,
    params = {},
    instanceKey = resource.identifyInstance(params),
    resolveWithStale = false,
    httpClient
  }) => {
    const promiseType = resolveWithStale ? 'stale' : 'fresh';
    const current = promiseCache.get(instanceKey, promiseType);
    if (current) {
      return current;
    }
    const {
      promise,
      resolve,
      reject
    } = withTrackedResolvers();
    const handleStateChange = state => {
      if (!state) {
        return false;
      }
      const {
        data,
        loading,
        error
      } = state;
      const canResolveWithStale = resolveWithStale && data != null;
      if (error && !canResolveWithStale) {
        reject(error);
        return true;
      } else if (!loading || canResolveWithStale) {
        resolve(state);
        return true;
      }
      return false;
    };
    if (handleStateChange(lifecycleManager.materialize({
      instanceKey,
      params,
      changeContext: changeManager.start('materialize')
    }))) {
      promiseCache.set(instanceKey, promiseType, promise);
      return promise;
    }
    watch({
      resource,
      params,
      instanceKey,
      httpClient
    }, (state, unwatch) => {
      if (handleStateChange(state)) {
        unwatch();
      }
    });
    promiseCache.set(instanceKey, promiseType, promise);
    return promise;
  };
  resourceRegistry.emitter.on('schemaChange', event => {
    lifecycleManager.resync({
      added: event.added,
      removed: event.removed,
      changeContext: changeManager.start('schema-change')
    });
  });
  devtools.registerHooks({
    resourceRegistry,
    resolverRegistry,
    lifecycleManager,
    pollManager,
    invalidate,
    setState,
    getPollGroupMembers: () => groupManager.getAllMembers(),
    persistenceManager
  });
  if (process.env.NODE_ENV !== 'production') {
    __mockInternal_registerClient({
      getInstancesForType: typeName => lifecycleManager.getInstancesForType(typeName)
    });
  }
  return {
    getState,
    watch,
    getPromise,
    invalidate,
    invalidateAll,
    setState,
    devtoolsId: devtools.devtoolsId
  };
};
let _singleton;

/**
 * Returns the module-level Pulse client, constructing it on first call.
 * Subsequent calls return the same instance — Pulse is intentionally a
 * singleton so hooks, libraries, and app code all share one cache.
 *
 * Options are honored on the first call only. Subsequent calls with
 * different options are silently ignored.
 */
export const createClient = __opts => {
  if (!_singleton) {
    _singleton = constructClient();
  }
  return _singleton;
};

/**
 * Returns the Pulse client singleton. This is the recommended way to obtain
 * a reference to the client outside of React (inside React, use the hooks).
 */
export const getPulseClient = () => createClient();
export const __testOnly_clearClient = () => {
  _singleton = undefined;
};